import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Reveal from "@/components/base/Reveal";
import Lightbox from "@/components/feature/Lightbox";
import usePageMeta from "@/hooks/usePageMeta";
import { filmStills, getWorkById, works } from "@/mocks/works";
import { workTypeLabel } from "@/utils/labels";

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const work = getWorkById(id ?? "");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [toast, setToast] = useState("");
  const toastTimer = useRef<number | null>(null);

  usePageMeta({
    title: work ? `翔田千里影视作品－${work.title}` : "作品未找到－翔田千里影视作品",
    description: work
      ? `${work.title}（${work.enTitle}）是翔田千里 ${work.year} 年出演的${workTypeLabel(work.type)}，由${work.director}执导，翔田千里在片中饰演${work.role}。${work.synopsis.slice(0, 60)}`
      : "该作品页面不存在，可返回翔田千里影视作品列表浏览全部作品。",
    keywords: work
      ? `${work.title},翔田千里,${work.genre.join(",")},${workTypeLabel(work.type)}介绍`
      : "翔田千里影视作品",
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const notify = (message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 2600);
  };

  if (!work) {
    return (
      <SiteLayout>
        <div className="container-page flex min-h-[60vh] flex-col items-center justify-center py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-error-warning-line" aria-hidden="true"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl text-foreground-950">没有找到这部作品</h1>
          <p className="mt-2 max-w-sm text-sm text-foreground-600">
            链接可能已失效，或者该作品尚未收录。你可以回到作品列表继续浏览。
          </p>
          <Link
            to="/works"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-600 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-700"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回作品列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const workIndex = works.findIndex((item) => item.id === work.id);
  const gallery = [0, 1, 2, 3].map(
    (offset) => filmStills[((workIndex < 0 ? 0 : workIndex) + offset) % filmStills.length],
  );
  const lightboxImages = [work.cover, ...gallery];

  const related = works
    .filter(
      (item) =>
        item.id !== work.id &&
        (item.type === work.type || item.genre.some((genre) => work.genre.includes(genre))),
    )
    .sort((a, b) => Math.abs(a.year - work.year) - Math.abs(b.year - work.year))
    .slice(0, 3);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notify("作品链接已复制，可粘贴到任意聊天窗口分享");
    } catch {
      notify("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-primary-950">
        <img
          src={work.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover object-top opacity-30 blur-md"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/90 to-primary-950/70"></div>
        <div className="container-page relative py-8 md:py-12">
          <Breadcrumb
            tone="dark"
            items={[
              { label: "首页", to: "/" },
              { label: "影视作品", to: "/works" },
              { label: work.title },
            ]}
          />

          <div className="mt-7 grid grid-cols-1 gap-6 md:grid-cols-[240px_1fr] md:gap-9 lg:gap-12">
            <button
              type="button"
              onClick={() => setLightboxIndex(0)}
              className="group relative aspect-[3/4] w-full max-w-[220px] cursor-zoom-in overflow-hidden rounded-lg border border-background-50/20 md:max-w-none"
              aria-label="查看作品海报大图"
            >
              <img
                src={work.cover}
                alt={`${work.title} 海报`}
                title={`${work.title} 翔田千里影视作品`}
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1.5 bg-primary-950/60 py-2 text-[11px] text-background-50 backdrop-blur">
                <i className="ri-zoom-in-line" aria-hidden="true"></i>
                点击查看大图
              </span>
            </button>

            <div className="flex flex-col justify-center">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-[11px] font-medium text-primary-950">
                  {workTypeLabel(work.type)}
                </span>
                <span className="rounded-md border border-background-50/25 px-2.5 py-1 text-[11px] text-background-50/80">
                  {work.year}
                </span>
                <span className="rounded-md border border-background-50/25 px-2.5 py-1 text-[11px] text-background-50/80">
                  {work.roleType}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-3xl leading-tight text-background-50 md:text-[42px]">
                {work.title}
              </h1>
              <p className="mt-2 text-xs uppercase tracking-wider2 text-background-50/50">{work.enTitle}</p>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-50/75">{work.synopsis}</p>

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-background-50/70">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-star-line text-accent-400" aria-hidden="true"></i>
                  饰 {work.role}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-movie-2-line text-accent-400" aria-hidden="true"></i>
                  {work.director} 执导
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-accent-400" aria-hidden="true"></i>
                  {work.duration}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-400" aria-hidden="true"></i>
                  评分 {work.rating.toFixed(1)}
                </span>
              </div>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  onClick={() => setLightboxIndex(0)}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400"
                >
                  <i className="ri-image-line" aria-hidden="true"></i>
                  浏览剧照
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-link" aria-hidden="true"></i>
                  复制链接
                </button>
                <Link
                  to="/works"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true"></i>
                  作品列表
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-10 md:py-14">
        <div className="container-page grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px] lg:gap-10">
          <div className="space-y-6">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
              <h2 className="font-heading text-xl text-foreground-950">剧情简介</h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700">{work.synopsis}</p>
              <p className="mt-3 text-sm leading-loose text-foreground-600">
                本片是翔田千里与{work.director}的又一次合作。她在片中饰演{work.role}，以{work.genre.join("、")}
                类型的叙事方式呈现人物的内心变化，全片共有 {work.duration} 的篇幅用于铺陈细节。
              </p>

              <div className="mt-6 grid grid-cols-2 gap-4 border-t border-background-200/70 pt-5 sm:grid-cols-3">
                <div>
                  <p className="text-[11px] text-foreground-400">上映日期</p>
                  <p className="mt-1 text-sm text-foreground-900">{work.releaseDate}</p>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-400">导演</p>
                  <p className="mt-1 text-sm text-foreground-900">{work.director}</p>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-400">角色</p>
                  <p className="mt-1 text-sm text-foreground-900">{work.role}</p>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-400">作品类型</p>
                  <p className="mt-1 text-sm text-foreground-900">{workTypeLabel(work.type)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-400">篇幅</p>
                  <p className="mt-1 text-sm text-foreground-900">{work.duration}</p>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-400">观众评分</p>
                  <p className="mt-1 text-sm text-foreground-900">{work.rating.toFixed(1)} / 10</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-heading text-xl text-foreground-950">剧照与海报</h2>
                <span className="text-[11px] text-foreground-400">点击任意图片可放大预览</span>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                {lightboxImages.map((image, index) => (
                  <button
                    key={`${image}-${index}`}
                    type="button"
                    onClick={() => setLightboxIndex(index)}
                    className={`group relative cursor-zoom-in overflow-hidden rounded-md bg-primary-900 ${
                      index === 0 ? "col-span-2 aspect-[16/10] md:col-span-2" : "aspect-[4/3]"
                    }`}
                    aria-label={`查看第 ${index + 1} 张图片`}
                  >
                    <img
                      src={image}
                      alt={`${work.title} 剧照 ${index + 1}`}
                      title={`${work.title} 翔田千里剧照`}
                      className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-primary-950/0 text-background-50 opacity-0 transition-all group-hover:bg-primary-950/30 group-hover:opacity-100">
                      <i className="ri-zoom-in-line text-2xl" aria-hidden="true"></i>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
              <h2 className="font-heading text-xl text-foreground-950">分享这部作品</h2>
              <p className="mt-2 text-xs text-foreground-500">分享功能为前端演示，复制链接后可自行粘贴到社交平台。</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-700"
                >
                  <i className="ri-file-copy-line" aria-hidden="true"></i>
                  复制链接
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300/80 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-weibo-line" aria-hidden="true"></i>
                  分享到微博
                </button>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-background-300/80 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-twitter-x-line" aria-hidden="true"></i>
                  分享到 X
                </button>
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-[11px] uppercase tracking-wider2 text-foreground-400">分类与标签</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {work.genre.map((genre) => (
                  <Link
                    key={genre}
                    to={`/search?q=${encodeURIComponent(genre)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-[11px] text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {genre}
                  </Link>
                ))}
                <Link
                  to={`/works?type=${work.type}`}
                  className="rounded-full bg-primary-100 px-3 py-1 text-[11px] text-primary-800 transition-colors hover:bg-primary-200"
                >
                  {workTypeLabel(work.type)}
                </Link>
                <Link
                  to={`/works?role=${encodeURIComponent(work.roleType)}`}
                  className="rounded-full bg-accent-100 px-3 py-1 text-[11px] text-accent-900 transition-colors hover:bg-accent-200"
                >
                  {work.roleType}
                </Link>
              </div>
            </div>

            <div className="rounded-lg bg-primary-950 p-5 text-background-50">
              <h2 className="font-heading text-base">演员资料</h2>
              <p className="mt-2 text-xs leading-relaxed text-background-50/70">
                查看翔田千里的完整作品年表、职业经历与荣誉记录。
              </p>
              <Link
                to="/profile"
                className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-xs text-primary-950 transition-colors hover:bg-accent-400"
              >
                前往个人资料
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="text-[11px] uppercase tracking-wider2 text-foreground-400">相关动态</h2>
              <ul className="mt-3 space-y-3">
                <li>
                  <Link to="/news" className="group flex items-start gap-2 text-xs leading-relaxed text-foreground-700">
                    <i className="ri-article-line mt-0.5 text-foreground-400" aria-hidden="true"></i>
                    <span className="group-hover:text-accent-700">相关作品资讯与访谈持续更新中</span>
                  </Link>
                </li>
                <li>
                  <Link to="/news" className="group flex items-start gap-2 text-xs leading-relaxed text-foreground-700">
                    <i className="ri-calendar-event-line mt-0.5 text-foreground-400" aria-hidden="true"></i>
                    <span className="group-hover:text-accent-700">查看全部活动报道与放映信息</span>
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-page">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-px w-7 bg-accent-500"></span>
                <span className="text-[11px] uppercase tracking-wider2 text-accent-600">Related Works</span>
              </div>
              <h2 className="mt-3 font-heading text-2xl text-foreground-950">相关作品推荐</h2>
              <p className="mt-2 text-xs text-foreground-500">依据作品类型、题材标签与上映年份匹配。</p>
            </div>
            <Link
              to="/works"
              className="inline-flex items-center gap-2 rounded-md border border-background-300/80 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              查看全部作品
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
            {related.map((item, index) => (
              <Reveal key={item.id} delay={index * 60}>
                <WorkCard work={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxIndex !== null && (
        <Lightbox
          images={lightboxImages}
          title={`${work.title} · 剧照预览`}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[90] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 animate-fade-in rounded-lg bg-primary-950 px-4 py-3 text-center text-xs text-background-50">
          <i className="ri-check-line mr-1.5 text-accent-400" aria-hidden="true"></i>
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}